import { useCallback, useRef, useEffect } from 'react';
import { useToolContext } from '../contexts/ToolContext';
import type { ToolOptions } from '../lib/tool-groups';
import { DEFAULT_TOOL_OPTIONS } from '../lib/tool-groups';
import { usePersistedState } from './usePersistedState';

export interface UseToolOptionsReturn {
  options: ToolOptions;
  setOption: <K extends keyof ToolOptions>(key: K, value: ToolOptions[K]) => void;
  /** Ref for canvas callbacks (avoids re-render) */
  optionsRef: React.RefObject<ToolOptions>;
}

/**
 * Typed access to tool options with persistence and ref-sync.
 *
 * Reads from ToolContext.state.options, writes via dispatch,
 * and persists to localStorage via usePersistedState.
 */
export function useToolOptions(): UseToolOptionsReturn {
  const { state, dispatch } = useToolContext();
  const options = state.options;

  // Ref for canvas render callbacks (avoids re-render dependency)
  const optionsRef = useRef<ToolOptions>(options);
  optionsRef.current = options;

  // Persistence: load from localStorage on mount, sync on change
  const [persisted, setPersisted] = usePersistedState<ToolOptions>(
    'tool.options',
    DEFAULT_TOOL_OPTIONS,
  );

  // On mount: hydrate ToolContext from persisted state
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      dispatch({ type: 'tool:setOptions', options: persisted });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setOption = useCallback(
    <K extends keyof ToolOptions>(key: K, value: ToolOptions[K]) => {
      dispatch({ type: 'tool:setOption', key, value });
      setPersisted((prev) => ({ ...prev, [key]: value }));
    },
    [dispatch, setPersisted],
  );

  return { options, setOption, optionsRef };
}
